"use client";

import { DatePicker } from "@/components/ui/date-picker";
import PageHeader from "@/components/layout/PageHeader";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { get, post, put, del, downloadFile } from "../../utils/service";
import { formatMoney, formatDate, parseApiError, today, isAdminUser, isFinanceUser, showMoney, originalHint, MISTAKE_REASONS } from "@/utils/finance";
import StatusBadge from "@/components/finance/StatusBadge";
import Modal, { btnPrimary, btnGhost, btnDanger, inputCls, labelCls } from "@/components/finance/Modal";

interface Payment { id: string; display_currency?: string; display_amount?: string | null; amount: string; currency: string; paid_date: string; reference: string | null; method: string | null; notes: string | null; recorder?: { first_name: string; last_name: string } | null }
interface Invoice {
  id: string; invoice_number: string; description: string | null; status: string; effective_status: string; amount: string; amount_paid: string; balance: string;
  currency: string; issued_date: string; due_date: string; paid_date: string | null; period_start: string | null; period_end: string | null; notes: string | null;
  is_backdated: boolean; created_at: string; suspected_mistake?: string | null;
  display_currency?: string; display_amount?: string | null; display_amount_paid?: string | null; display_balance?: string | null;
  client: { id: string; company_name: string; contact_person: string; email: string };
  contract: { id: string; service_type: string; start_date: string; billing_cycle: string } | null;
  payments: Payment[];
}

const METHODS = ["bank_transfer", "mobile_money", "cash", "card", "cheque", "other"];

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const admin = isAdminUser(); // deleting invoices is admin-only
  const finance = isFinanceUser(); // admins and managers settle invoices
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [missing, setMissing] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [confirm, setConfirm] = useState<"paid" | "cancelled" | "delete" | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [tab, setTab] = useState<"invoice" | "history" | "notes">("invoice");
  const [notes, setNotes] = useState<string | null>(null);
  const [pay, setPay] = useState({ amount: "", paid_date: today(), reference: "", method: "bank_transfer", notes: "" });

  const load = useCallback(async () => {
    try {
      const r = await get<{ data: { invoice: Invoice } }>(`/invoices/${id}`);
      setInvoice(r.data.invoice);
    } catch {
      setMissing(true);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (missing) return <div className="p-10 text-center text-sm text-gray-500">Invoice not found. <Link className="text-primary underline" to="/invoices">Back to invoices</Link></div>;
  if (!invoice) return <div className="p-10 animate-pulse"><div className="h-6 w-48 bg-gray-100 rounded mb-4" /><div className="h-40 bg-gray-100 rounded" /></div>;

  const open = parseFloat(invoice.balance) > 0 && invoice.status !== "cancelled";
  const overdue = invoice.effective_status === "overdue";
  const daysLate = overdue ? Math.max(1, Math.floor((Date.now() - new Date(`${invoice.due_date}T00:00:00`).getTime()) / 86400000)) : 0;
  const fmt = (v: string) => formatMoney(v, invoice.currency); // document currency (used by the PDF preview and payment form)

  const openPay = () => { setPay((p) => ({ ...p, amount: invoice.balance })); setError(""); setFieldErrors({}); setPayOpen(true); };

  const submitPayment = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setFieldErrors({});
    try {
      await post(`/invoices/${invoice.id}/payments`, { ...pay, reference: pay.reference || undefined, notes: pay.notes || undefined });
      setPayOpen(false);
      await load();
    } catch (err) {
      const f = parseApiError(err);
      setError(f.message);
      setFieldErrors(f.fieldErrors);
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async () => {
    if (!confirm) return;
    setBusy(true);
    setError("");
    try {
      if (confirm === "delete") {
        await del(`/invoices/${invoice.id}`);
        navigate(`/invoices/client/${invoice.client.id}`);
        return;
      }
      await put(`/invoices/${invoice.id}`, { status: confirm });
      setConfirm(null);
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const saveNotes = async () => {
    setBusy(true);
    setError("");
    try {
      await put(`/invoices/${invoice.id}`, { notes: notes ?? "" });
      setNotes(null);
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const Stat = ({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) => (
    <div><p className="text-xs text-gray-500">{label}</p><p className={`mt-1 text-lg font-semibold ${tone ?? "text-gray-900"}`}>{value}</p>{sub && <p className="text-[11px] text-gray-400">{sub}</p>}</div>
  );

  const lineDescription = invoice.description || (invoice.contract ? invoice.contract.service_type.replace(/_/g, " ") : "Services");

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <PageHeader title={invoice.invoice_number} description={`${invoice.client.company_name}${invoice.description ? ` · ${invoice.description}` : ""}`}>
        <Link to={`/invoices/client/${invoice.client.id}`} className={btnGhost}>Client invoices</Link>
        <button className={btnPrimary} onClick={() => downloadFile(`/invoices/${invoice.id}/pdf`, `${invoice.invoice_number}.pdf`)}>Generate PDF invoice</button>
      </PageHeader>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* ---------------- left: details ---------------- */}
        <div className="min-w-0">
          <div className="flex gap-6 border-b border-gray-200 text-sm">
            {([["invoice", "Invoice"], ["history", "History"], ["notes", "Notes"]] as const).map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`-mb-px pb-2.5 border-b-2 transition ${tab === k ? "border-primary text-gray-900 font-medium" : "border-transparent text-gray-500 hover:text-gray-800"}`}>{l}</button>
            ))}
          </div>

          {tab === "invoice" && (
            <div className="pt-5 space-y-7">
              {invoice.suspected_mistake && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <p className="font-semibold">This invoice looks like it was generated by mistake</p>
                  <p className="mt-0.5 text-xs">{MISTAKE_REASONS[invoice.suspected_mistake] ?? invoice.suspected_mistake}. {admin ? "It has no payments, so you can delete it." : "Ask an administrator to delete it."}</p>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <StatusBadge kind="invoice" status={invoice.effective_status} />
                {overdue && <span className="text-xs font-medium text-red-600">{daysLate} day{daysLate === 1 ? "" : "s"} delay</span>}
                {invoice.is_backdated && <span className="text-xs text-gray-500">Backdated · historical period</span>}
                <div className="ml-auto flex gap-2">
                  {finance && open && <button className={btnGhost} onClick={() => setConfirm("paid")}>Mark remaining as paid</button>}
                  {finance && invoice.status !== "cancelled" && parseFloat(invoice.amount_paid) === 0 && <button className={btnGhost} onClick={() => setConfirm("cancelled")}>Cancel invoice</button>}
                  {admin && parseFloat(invoice.amount_paid) === 0 && <button className={btnDanger} onClick={() => setConfirm("delete")}>Delete invoice</button>}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                <Stat label="Total amount" value={showMoney(invoice, "amount")} sub={originalHint(invoice, "amount") && `billed ${originalHint(invoice, "amount")}`} tone={overdue ? "text-primary" : undefined} />
                <Stat label="Open amount" value={showMoney(invoice, "balance")} sub={originalHint(invoice, "balance") && originalHint(invoice, "balance")} />
                <Stat label="Paid" value={showMoney(invoice, "amount_paid")} />
                <Stat label="Due date" value={formatDate(invoice.due_date)} />
                <Stat label="Paid on" value={invoice.paid_date ? formatDate(invoice.paid_date) : "-"} />
                <Stat label="Invoice date" value={formatDate(invoice.issued_date)} />
              </div>

              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div><dt className="text-[11px] uppercase tracking-wide text-gray-500">Client</dt><dd className="mt-0.5"><Link className="text-primary underline" to={`/invoices/client/${invoice.client.id}`}>{invoice.client.company_name}</Link></dd></div>
                <div><dt className="text-[11px] uppercase tracking-wide text-gray-500">Contract</dt><dd className="mt-0.5">{invoice.contract ? <span className="capitalize">{invoice.contract.service_type.replace(/_/g, " ")} <span className="text-gray-500">(effective {formatDate(invoice.contract.start_date)})</span></span> : "One-off invoice"}</dd></div>
                <div><dt className="text-[11px] uppercase tracking-wide text-gray-500">Billing period</dt><dd className="mt-0.5">{invoice.period_start ? `${formatDate(invoice.period_start)} – ${formatDate(invoice.period_end)}` : "—"}</dd></div>
                <div><dt className="text-[11px] uppercase tracking-wide text-gray-500">Invoice currency</dt><dd className="mt-0.5">{invoice.currency}</dd></div>
              </dl>

              <section>
                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                  <h2 className="text-base font-semibold text-gray-900">Payments</h2>
                  {finance && open && <button onClick={openPay} className="inline-flex items-center gap-1 rounded-lg border border-primary px-4 py-1.5 text-xs font-medium text-primary hover:bg-primary/10">+ Add</button>}
                </div>
                {invoice.payments.length ? invoice.payments.map((p) => (
                  <div key={p.id} className="flex items-start justify-between gap-4 border-b border-gray-100 py-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{showMoney(p, "amount")}</p>
                      <p className="text-xs text-gray-500">Paid on {formatDate(p.paid_date)}{originalHint(p, "amount") ? ` · ${originalHint(p, "amount")}` : ""}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900 capitalize">{p.method?.replace(/_/g, " ") ?? "Payment"}</p>
                      <p className="text-xs text-gray-500">{p.reference ?? "No reference"} · {p.recorder ? `${p.recorder.first_name} ${p.recorder.last_name}` : "System"}</p>
                    </div>
                  </div>
                )) : <p className="py-6 text-sm text-gray-500">No payments recorded yet.</p>}
              </section>
            </div>
          )}

          {tab === "history" && (
            <ol className="pt-5 space-y-4">
              {[
                ...invoice.payments.map((p) => ({ at: p.paid_date, title: `Payment of ${showMoney(p, "amount")}`, sub: `${p.method?.replace(/_/g, " ") ?? "payment"}${p.reference ? ` · ${p.reference}` : ""}` })),
                { at: invoice.issued_date, title: "Invoice issued", sub: `Due ${formatDate(invoice.due_date)}` },
                { at: invoice.created_at.slice(0, 10), title: invoice.is_backdated ? "Generated (backdated period)" : "Created in the system", sub: "" },
              ].sort((a, b) => (a.at < b.at ? 1 : -1)).map((ev, i) => (
                <li key={i} className="flex gap-3"><span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" /><div><p className="text-sm font-medium text-gray-900">{ev.title}</p><p className="text-xs text-gray-500">{formatDate(ev.at)}{ev.sub ? ` · ${ev.sub}` : ""}</p></div></li>
              ))}
            </ol>
          )}

          {tab === "notes" && (
            <div className="pt-5 space-y-3">
              {finance ? (
                <>
                  <textarea className={inputCls} rows={6} value={notes ?? invoice.notes ?? ""} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes about this invoice…" />
                  <div className="flex justify-end"><button className={btnPrimary} disabled={busy || notes === null} onClick={saveNotes}>{busy ? "Saving…" : "Save notes"}</button></div>
                  {error && <p className="text-xs text-red-600">{error}</p>}
                </>
              ) : <p className="text-sm text-gray-700 whitespace-pre-wrap">{invoice.notes || "No notes."}</p>}
            </div>
          )}
        </div>

        {/* ---------------- right: the invoice document ---------------- */}
        <aside className="min-w-0 rounded-xl bg-gray-100 p-4 sm:p-8">
          <div className="mx-auto w-full rounded-lg bg-white p-6 sm:p-8 shadow-xl text-[11px] leading-relaxed text-gray-800">
            <div className="flex items-start justify-between">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white font-bold">P</div>
              <div className="text-right">
                <p className="text-lg text-gray-700">Invoice</p>
                <p className="font-semibold text-gray-900">Invoice No: {invoice.invoice_number}</p>
                <p className="text-gray-500">Date issued: {formatDate(invoice.issued_date)}</p>
                <p className="text-gray-500">Due: {formatDate(invoice.due_date)}</p>
              </div>
            </div>
            <p className="mt-3 text-sm font-semibold text-gray-900">Pelar Hive Digitals</p>
            <p className="text-gray-500">Soser View Hotel Apartments<br />Katabi Town Council, Entebbe, Uganda</p>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <div><p className="text-primary">Customer</p><p className="font-semibold text-gray-900">{invoice.client.company_name}</p><p className="text-gray-500">{invoice.client.contact_person}<br />{invoice.client.email}</p></div>
              <div><p className="text-primary">Billing period</p><p className="font-semibold text-gray-900">{invoice.period_start ? `${formatDate(invoice.period_start)} – ${formatDate(invoice.period_end)}` : "One-off"}</p><p className="text-gray-500 capitalize">{invoice.status === "cancelled" ? "cancelled" : invoice.effective_status.replace(/_/g, " ")}</p></div>
            </div>

            <p className="mt-6 text-primary">Order details</p>
            <div className="mt-1 rounded bg-gray-100 px-3 py-1.5 flex justify-between font-medium text-gray-700"><span>Item</span><span>Amount</span></div>
            <div className="flex justify-between border-b border-gray-100 px-3 py-2.5"><span className="capitalize font-medium text-gray-900">{lineDescription}</span><span>{fmt(invoice.amount)}</span></div>

            <div className="mt-6 ml-auto w-56 space-y-1 text-right">
              <p>Total: <span className="font-medium">{fmt(invoice.amount)}</span></p>
              {parseFloat(invoice.amount_paid) > 0 && <p>Paid: <span className="font-medium">{fmt(invoice.amount_paid)}</span></p>}
              <p className="text-primary font-semibold">Balance due: {fmt(invoice.balance)}</p>
            </div>
            <p className="mt-8 text-gray-400">Thank you for your business. This document is issued in {invoice.currency}.</p>
          </div>
          <div className="mt-4 flex justify-center">
            <button className={btnPrimary} onClick={() => downloadFile(`/invoices/${invoice.id}/pdf`, `${invoice.invoice_number}.pdf`)}>Download PDF</button>
          </div>
        </aside>
      </div>

      <Modal open={payOpen} onClose={() => setPayOpen(false)} title="Record payment">
        <form onSubmit={submitPayment} className="space-y-3">
          <p className="text-xs text-gray-500">Remaining balance: <strong>{fmt(invoice.balance)}</strong></p>
          <div><label className={labelCls}>Amount ({invoice.currency})</label><input className={inputCls} type="number" step="0.01" min="0.01" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} required />{fieldErrors.amount && <p className="mt-1 text-xs text-red-600">{fieldErrors.amount}</p>}</div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Payment date</label><DatePicker value={pay.paid_date} onChange={(v) => setPay({ ...pay, paid_date: v })} max={today()} required />{fieldErrors.paid_date && <p className="mt-1 text-xs text-red-600">{fieldErrors.paid_date}</p>}</div>
            <div><label className={labelCls}>Method</label><select className={inputCls} value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>{METHODS.map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}</select></div>
          </div>
          <div><label className={labelCls}>Reference</label><input className={inputCls} value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} placeholder="Transaction / receipt no." /></div>
          <div><label className={labelCls}>Notes</label><input className={inputCls} value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} /></div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-1"><button type="button" className={btnGhost} onClick={() => setPayOpen(false)}>Cancel</button><button className={btnPrimary} disabled={busy}>{busy ? "Saving…" : "Record payment"}</button></div>
        </form>
      </Modal>

      <Modal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm === "paid" ? "Mark as paid?" : confirm === "delete" ? "Delete invoice?" : "Cancel invoice?"}
        footer={<><button className={btnGhost} onClick={() => setConfirm(null)}>Back</button><button className={confirm === "cancelled" || confirm === "delete" ? btnDanger : btnPrimary} disabled={busy} onClick={changeStatus}>{busy ? "Working…" : "Confirm"}</button></>}
      >
        <p className="text-xs text-gray-600">
          {confirm === "paid" ? `This records a payment of ${fmt(invoice.balance)} dated today for the remaining balance, so the payment history stays accurate.` : confirm === "delete" ? `${invoice.invoice_number} will be permanently removed and its billing period becomes available to generate again if it has ended. A copy is kept in the audit log. Invoices with payments can never be deleted.` : "The invoice stays on record but is voided. This is only possible while no payments have been recorded."}
        </p>
        {error && <p className="text-xs text-red-600">{error}</p>}
      </Modal>
    </div>
  );
}
